import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

/**
 * White-label cleanup. The hosting platform server-side injects:
 *  - a fixed-position "Made with Emergent" badge (#emergent-badge)
 *  - the assets.emergent.sh main script that re-hydrates the badge
 *  - PostHog telemetry tied to the platform's analytics
 *  - a default <title> + meta-description
 *
 * We strip them on every load so DARA presents as a fully white-labeled
 * product to end customers. A MutationObserver also re-runs the cleanup
 * if the platform tries to re-inject the badge after initial render.
 */
const DARA_TITLE = "DARA — Enterprise AI Data Monetization";
const DARA_DESCRIPTION = "DARA is the command terminal for enterprise data monetization. Capture every signal, compound the revenue.";

function whitelabelCleanup() {
  const badge = document.getElementById("emergent-badge");
  if (badge && badge.parentNode) badge.parentNode.removeChild(badge);
  document.querySelectorAll('script[src*="emergent.sh"]').forEach((s) => s.remove());
  // Phase 25.16af — only revert title/description if they've been HIJACKED
  // to Emergent-related content. Per-route React customizations (e.g.
  // /why-dara setting its own title for SEO) are now preserved instead
  // of being stomped on every React render.
  if (/emergent/i.test(document.title) && document.title !== DARA_TITLE) {
    document.title = DARA_TITLE;
  }
  const meta = document.querySelector('meta[name="description"]');
  if (meta) {
    const cur = meta.getAttribute("content") || "";
    if (/emergent/i.test(cur) && cur !== DARA_DESCRIPTION) {
      meta.setAttribute("content", DARA_DESCRIPTION);
    }
  }
}

// First-pass cleanup
whitelabelCleanup();

// Disable platform PostHog analytics (drops events instead of sending them).
try {
  if (typeof window !== "undefined") {
    const noop = () => {};
    window.posthog = {
      init: noop,
      capture: noop,
      identify: noop,
      register: noop,
      reset: noop,
      onFeatureFlags: noop,
      isFeatureEnabled: () => false,
      getFeatureFlag: () => undefined,
      startSessionRecording: noop,
      stopSessionRecording: noop,
      __SV: 1,
    };
  }
} catch { /* noop */ }

// Re-run cleanup whenever the DOM mutates so a re-injected badge is removed.
if (typeof MutationObserver !== "undefined") {
  const obs = new MutationObserver(() => whitelabelCleanup());
  obs.observe(document.documentElement, { childList: true, subtree: true });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
